// Evidence presentation shared by every feature: blockers with their rule/decision
// references, signature lines, eligibility, and the event history.
import { useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useState } from 'react'
import { eventKindLabel, ROLES } from '@/domain/catalogue'
import { OM_APPROVAL_REQUIRED_MESSAGE } from '@/domain/omApproval'
import type { AttestationView, Blocker, Eligibility, PermitBundle } from '@/domain/types'
import { PtwError } from '@/lib/commands'
import { CodeSignInForm } from '@/features/account/SignIn'
import { useSession } from '@/lib/queries'
import { signInAgain } from '@/lib/session'
import { fmtDateTime, shortDigest } from '@/lib/format'
import { Badge, Banner, Button, cx } from './ui'

export function RuleRefs({ ruleIds, decisionIds }: { ruleIds?: string[]; decisionIds?: string[] }) {
  const all = [...(ruleIds ?? []), ...(decisionIds ?? [])]
  if (all.length === 0) return null
  return (
    <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
      {all.map((id) => (
        <span key={id} className={cx('code rounded px-1 text-[11px]', id.startsWith('DEC') ? 'bg-warn-light text-warn' : 'bg-brand-light text-brand-dark')}>
          {id}
        </span>
      ))}
    </span>
  )
}

/**
 * Plain words for a blocker that reached the browser without a message. The server sends
 * a message with every blocker; this only keeps a blank one from showing as an empty line.
 */
const BLOCKER_FALLBACK: Record<string, string> = {
  WORK_PARTY_EXCEEDS_MAXIMUM: 'More people are in the work party than the permit allows.',
  AREA_CLASSIFICATION_CHANGED: 'The area classification has changed since it was confirmed. Check it again.',
  WORK_TYPE_TASK_MISMATCH: 'A task does not match the chosen work type.',
  OM_APPROVAL_REQUIRED: OM_APPROVAL_REQUIRED_MESSAGE,
  OM_APPROVAL_ALREADY_RECORDED: 'The Operations Manager has already approved this authorisation.',
  OM_APPROVAL_NOT_IN_POLICY: "This permit's policy does not include an Operations Manager approval.",
}

export function blockerText(b: Pick<Blocker, 'code' | 'message'>): string {
  const message = typeof b.message === 'string' ? b.message.trim() : ''
  if (message) return message
  return BLOCKER_FALLBACK[b.code] ?? `This step is blocked (${String(b.code ?? 'no code').replace(/_/g, ' ').toLowerCase()}).`
}

export function BlockerList({ blockers }: { blockers: Blocker[] }) {
  if (blockers.length === 0) return null
  return (
    <ul className="space-y-2">
      {blockers.map((b, i) => (
        <li key={`${b.code}-${i}`} className="rounded-lg border border-line bg-white p-2 text-sm">
          <span className="font-semibold text-ink">{blockerText(b)}</span>
          <span className="code ml-2 text-xs text-ink-faint">{b.code}</span>
          <RuleRefs ruleIds={b.rule_ids} decisionIds={b.decision_ids} />
        </li>
      ))}
    </ul>
  )
}

/** Renders any command failure: blockers when present, otherwise the plain explanation. */
export function ErrorBanner({ error, title }: { error: unknown; title?: string }) {
  if (!error) return null
  const e = PtwError.from(error)
  // The persona was signed in on another device (or the hour ran out): offer to sign in
  // again right here, so whatever is filled in on this screen stays and can be sent again.
  if (e.needsSignInAgain) return <SignInAgainBanner error={e} />
  return (
    <Banner tone={e.code === 'PW001' ? 'warn' : 'bad'} title={title ?? e.summary}>
      {e.blockers.length > 0 ? (
        <BlockerList blockers={e.blockers} />
      ) : (
        <p>
          {e.problem ? `${e.field ? `${e.field}: ` : ''}${e.problem}` : e.message}
          <span className="code ml-2 text-xs text-ink-faint">{e.code}</span>
        </p>
      )}
    </Banner>
  )
}

/**
 * The permit changed while an action panel was open: what changed (from the event log),
 * and a deliberate confirmation before the person may sign. Pair with useSeenVersion.
 */
export function PermitChangedBanner({ changes, onAcknowledge }: { changes: PermitBundle['events']; onAcknowledge: () => void }) {
  return (
    <Banner tone="warn" title="The permit changed while this form was open">
      {changes.length > 0 ? (
        <ul className="mb-2 list-disc space-y-1 pl-5">
          {changes.map((e) => (
            <li key={e.sequence}>
              {eventKindLabel(e.event_kind)}
              {e.actor ? ` by ${e.actor.display_name}` : ''}, {fmtDateTime(e.server_recorded_at)}
            </li>
          ))}
        </ul>
      ) : null}
      <p>This form now shows the latest record. Read it again, then confirm to continue.</p>
      <Button type="button" variant="secondary" className="mt-2" onClick={onAcknowledge}>
        I have checked the changes
      </Button>
    </Banner>
  )
}

export function SignatureLine({ a, label }: { a: AttestationView | null | undefined; label?: ReactNode }) {
  if (!a) {
    return (
      <div className="rounded-lg border border-dashed border-line-strong p-2 text-sm text-ink-faint">
        {label ? <span className="font-semibold">{label}: </span> : null}
        Not signed
      </div>
    )
  }
  const role = ROLES[a.signing_role as keyof typeof ROLES]?.label ?? a.signing_role
  return (
    <div className="rounded-lg border border-line bg-white p-2 text-sm">
      {label ? <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">{label}</p> : null}
      <p className="text-ink">
        <span className="font-semibold">{a.signer.display_name}</span>
        {a.signer.organisation ? <span className="text-ink-soft"> ({a.signer.organisation})</span> : null}
        <span className="text-ink-soft"> signed as {role}</span>
      </p>
      <p className="mt-0.5 text-xs text-ink-soft">
        {fmtDateTime(a.server_signed_at)} - server time
        <span className="code ml-2">#{shortDigest(a.content_digest)}</span>
        {a.credential ? (
          <span className="ml-2">
            via {a.credential.role_code} {a.credential.credential_kind.replace('_', ' ')}
            {a.credential.expires_on ? ` to ${a.credential.expires_on}` : ''}
          </span>
        ) : null}
        {a.is_synthetic ? <Badge tone="synthetic" className="ml-2">synthetic</Badge> : null}
      </p>
    </div>
  )
}

const ELIGIBILITY_TEXT: Record<Eligibility['status'], { label: string; tone: 'ok' | 'bad' | 'warn' }> = {
  recorded_conditions_satisfied: { label: 'Recorded conditions satisfied - work may proceed', tone: 'ok' },
  blocked: { label: 'Work must not proceed', tone: 'bad' },
  unresolved: { label: 'Work must not proceed - a rule or value is unresolved', tone: 'warn' },
}

export function EligibilityBadge({ e }: { e: Eligibility }) {
  const t = ELIGIBILITY_TEXT[e.status]
  return <Badge tone={t.tone}>{e.status === 'recorded_conditions_satisfied' ? 'Work may proceed' : 'Work must not proceed'}</Badge>
}

export function EligibilityPanel({ e }: { e: Eligibility }) {
  const t = ELIGIBILITY_TEXT[e.status]
  return (
    <Banner tone={t.tone} title={t.label}>
      <p className="text-xs text-ink-soft">
        Evaluated by the server at {fmtDateTime(e.evaluated_at)}. This is a record check, not a substitute for the PH being
        present and the site being safe.
      </p>
      {e.blockers.length > 0 ? (
        <div className="mt-2">
          <BlockerList blockers={e.blockers} />
        </div>
      ) : null}
    </Banner>
  )
}

export function EventTimeline({ events }: { events: PermitBundle['events'] }) {
  return (
    <ol className="relative space-y-3 border-l-2 border-line pl-4">
      {[...events].reverse().map((ev) => (
        <li key={ev.sequence} className="text-sm">
          <span className="absolute -left-[7px] mt-1.5 h-3 w-3 rounded-full bg-brand" aria-hidden />
          <p className="font-semibold text-ink">
            {eventKindLabel(ev.event_kind)}
            <span className="code ml-2 text-xs font-normal text-ink-faint">#{ev.sequence}</span>
          </p>
          <p className="text-xs text-ink-soft">
            {fmtDateTime(ev.server_recorded_at)} - {ev.actor ? ev.actor.display_name : ev.actor_kind}
            {ev.new_row_version ? <span className="code ml-2">v{ev.prior_row_version}&rarr;v{ev.new_row_version}</span> : null}
          </p>
        </li>
      ))}
    </ol>
  )
}

function SignInAgainBanner({ error }: { error: PtwError }) {
  // Account builds: the same person enters a new emailed code here (0022); the screen and
  // what is filled in stay, and only that person can continue.
  const session = useSession()
  if (session.accountMode) {
    return (
      <Banner tone="warn" title={error.summary}>
        <p className="mb-2">Your sign-in lasted one hour. Nothing was sent or signed. What you filled in here stays.</p>
        {session.account ? <CodeSignInForm lockedEmail={session.account.email} /> : null}
      </Banner>
    )
  }
  return <PersonaSignInAgainBanner error={error} />
}

function PersonaSignInAgainBanner({ error }: { error: PtwError }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  async function again() {
    setBusy(true)
    setFailed(null)
    try {
      await signInAgain()
      setDone(true)
      void queryClient.invalidateQueries()
    } catch (err) {
      setFailed(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }
  if (done) {
    return (
      <Banner tone="ok" title="Signed in again">
        Nothing was sent before. Check what you filled in and press the button again.
      </Banner>
    )
  }
  return (
    <Banner tone="warn" title={error.summary}>
      <p>{error.code === 'SIGN_IN_EXPIRED' ? 'Your sign-in lasted one hour.' : 'Only one device can act as a persona at a time: the most recent sign-in wins.'} Nothing was sent or signed. What you filled in here stays.</p>
      {failed ? <p className="mt-1 font-semibold text-bad">{failed}</p> : null}
      <div className="mt-2">
        <Button onClick={again} loading={busy} data-testid="sign-in-again-here">
          Sign in again here
        </Button>
      </div>
    </Banner>
  )
}
